import { useEffect, useState } from 'react';
import axios from 'axios';
import { Wallet, Plus, RefreshCw, Loader2, Trash2, ExternalLink, Copy, Check, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const WALLET_TYPES = [
  { value: 'solana', label: 'Solana', icon: '◎', color: 'purple' },
  { value: 'ethereum', label: 'Ethereum', icon: 'Ξ', color: 'blue' },
];

export default function CointrackerWallets() {
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [walletToDelete, setWalletToDelete] = useState(null);
  const [coinbaseStatus, setCoinbaseStatus] = useState({ configured: false });
  const [importingCoinbase, setImportingCoinbase] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  
  // Form state
  const [newWallet, setNewWallet] = useState({
    name: '',
    address: '',
    chain: 'solana',
  });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchWallets();
    checkCoinbaseStatus();
  }, []);

  const fetchWallets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/wallets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallets(response.data.wallets || []);
    } catch (error) {
      console.error('Failed to fetch wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCoinbaseStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cointracker/coinbase/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoinbaseStatus(response.data);
    } catch (error) {
      console.error('Failed to check Coinbase status:', error);
    }
  };

  const handleImportCoinbase = async () => {
    setImportingCoinbase(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/coinbase/import`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Imported ${response.data.imported} Coinbase account(s)`);
      fetchWallets();
    } catch (error) {
      console.error('Failed to import Coinbase:', error);
      toast.error(error.response?.data?.detail || 'Failed to import Coinbase accounts');
    } finally {
      setImportingCoinbase(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cointracker/wallets/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Synced ${response.data.synced} wallet(s)`);
      if (response.data.errors?.length > 0) {
        toast.warning(`Failed to sync: ${response.data.errors.join(', ')}`);
      }
      fetchWallets();
    } catch (error) {
      console.error('Failed to sync all:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync wallets');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleAddWallet = async () => {
    if (!newWallet.name || !newWallet.address) {
      toast.error('Please fill in all fields');
      return;
    }

    setAdding(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/wallets`, newWallet, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wallet added successfully');
      setShowAddDialog(false);
      setNewWallet({ name: '', address: '', chain: 'solana' });
      fetchWallets();
    } catch (error) {
      console.error('Failed to add wallet:', error);
      toast.error(error.response?.data?.detail || 'Failed to add wallet');
    } finally {
      setAdding(false);
    }
  };

  const handleSyncWallet = async (walletId) => {
    setSyncing(prev => ({ ...prev, [walletId]: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/wallets/${walletId}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wallet synced');
      fetchWallets();
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to sync wallet');
    } finally {
      setSyncing(prev => ({ ...prev, [walletId]: false }));
    }
  };

  const handleDeleteWallet = async (walletId) => {
    setDeleting(walletId);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/cointracker/wallets/${walletId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wallet deleted');
      setWalletToDelete(null);
      fetchWallets();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete wallet');
    } finally {
      setDeleting(null);
    }
  };

  const confirmDelete = (wallet) => {
    setWalletToDelete(wallet);
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
    toast.success('Address copied');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const getExplorerUrl = (wallet) => {
    if (wallet.source === 'coinbase') {
      return null; // Coinbase wallets don't have public addresses
    }
    if (wallet.chain === 'solana') {
      return `https://solscan.io/account/${wallet.address}`;
    } else if (wallet.chain === 'ethereum') {
      return `https://etherscan.io/address/${wallet.address}`;
    }
    return null;
  };

  const getWalletIcon = (wallet) => {
    if (wallet.source === 'coinbase') {
      return { icon: 'CB', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400' };
    }
    if (wallet.chain === 'solana') {
      return { icon: '◎', bgClass: 'bg-purple-500/20', textClass: 'text-purple-400' };
    }
    if (wallet.chain === 'ethereum') {
      return { icon: 'Ξ', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400' };
    }
    return { icon: '●', bgClass: 'bg-zinc-500/20', textClass: 'text-zinc-400' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Wallets</h2>
          <p className="text-sm text-zinc-400 mt-1">{wallets.length} wallet{wallets.length !== 1 ? 's' : ''} tracked</p>
        </div>
        <div className="flex gap-2">
          {coinbaseStatus.configured && (
            <Button
              onClick={handleImportCoinbase}
              disabled={importingCoinbase}
              variant="outline"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            >
              {importingCoinbase ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Import Coinbase
            </Button>
          )}
          <Button
            onClick={handleSyncAll}
            disabled={syncingAll || wallets.length === 0}
            variant="outline"
            className="border-zinc-700"
          >
            {syncingAll ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync All
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-white text-black hover:bg-zinc-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Wallet
          </Button>
        </div>
      </div>

      {/* Coinbase Status Banner */}
      {!coinbaseStatus.configured && (
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-400 font-bold">CB</span>
                </div>
                <div>
                  <p className="text-white font-medium">Connect Coinbase</p>
                  <p className="text-sm text-zinc-400">Add your Coinbase API credentials in Emergent Secrets to import your accounts</p>
                </div>
              </div>
              <p className="text-xs text-zinc-500">COINBASE_API_KEY_NAME & COINBASE_PRIVATE_KEY</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Wallets Grid */}
      {wallets.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12">
            <div className="text-center">
              <Wallet className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No wallets yet</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Add a wallet to start tracking your crypto holdings
              </p>
              <Button onClick={() => setShowAddDialog(true)} variant="outline" className="border-zinc-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Wallet
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {wallets.map((wallet) => {
            const walletType = WALLET_TYPES.find(t => t.value === wallet.chain);
            const explorerUrl = getExplorerUrl(wallet);
            
            return (
              <Card key={wallet.wallet_id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                        wallet.chain === 'solana' ? 'bg-purple-500/20 text-purple-400' :
                        wallet.chain === 'ethereum' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {walletType?.icon || '●'}
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg">{wallet.name}</CardTitle>
                        <p className="text-xs text-zinc-500 capitalize">{wallet.chain}</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Balance */}
                  <div>
                    <p className="text-2xl font-bold text-white">{formatCurrency(wallet.balance_usd)}</p>
                    {wallet.last_synced && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Synced {new Date(wallet.last_synced).toLocaleString()}
                      </p>
                    )}
                  </div>

                  {/* Address */}
                  <div className="flex items-center gap-2 bg-zinc-800/50 p-2 rounded">
                    <code className="text-xs text-zinc-400 flex-1 truncate">
                      {wallet.address}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyAddress(wallet.address)}
                      className="h-6 w-6 p-0"
                    >
                      {copiedAddress === wallet.address ? (
                        <Check className="w-3 h-3 text-green-400" />
                      ) : (
                        <Copy className="w-3 h-3 text-zinc-400" />
                      )}
                    </Button>
                    {explorerUrl && (
                      <a
                        href={explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-6 w-6 flex items-center justify-center text-zinc-400 hover:text-white"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>

                  {/* Holdings Preview */}
                  {wallet.holdings && wallet.holdings.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Holdings</p>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {wallet.holdings.slice(0, 3).map((holding, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-zinc-400">{holding.symbol}</span>
                            <span className="text-white">{holding.amount?.toFixed(4)}</span>
                          </div>
                        ))}
                        {wallet.holdings.length > 3 && (
                          <p className="text-xs text-zinc-500">+{wallet.holdings.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2 border-t border-zinc-800">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSyncWallet(wallet.wallet_id)}
                      disabled={syncing[wallet.wallet_id]}
                      className="flex-1 border-zinc-700"
                    >
                      {syncing[wallet.wallet_id] ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Sync
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => confirmDelete(wallet)}
                      disabled={deleting === wallet.wallet_id}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      {deleting === wallet.wallet_id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Wallet Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add Wallet</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add a wallet address to track. We'll fetch your balances and transactions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Wallet Name</label>
              <Input
                placeholder="e.g., My Phantom Wallet"
                value={newWallet.name}
                onChange={(e) => setNewWallet(prev => ({ ...prev, name: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Chain</label>
              <Select
                value={newWallet.chain}
                onValueChange={(value) => setNewWallet(prev => ({ ...prev, chain: value }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {WALLET_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Wallet Address</label>
              <Input
                placeholder={newWallet.chain === 'solana' ? 'Enter Solana address...' : 'Enter Ethereum address (0x...)'}
                value={newWallet.address}
                onChange={(e) => setNewWallet(prev => ({ ...prev, address: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddWallet}
              disabled={adding}
              className="bg-white text-black hover:bg-zinc-200"
            >
              {adding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Wallet'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!walletToDelete} onOpenChange={() => setWalletToDelete(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Wallet</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to delete "{walletToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWalletToDelete(null)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteWallet(walletToDelete?.wallet_id)}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
